<script lang="ts">
  import { onMount } from "svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { mdiRefresh } from "@mdi/js"
  import { buildWorkingHoursByUser } from "../../../../../../../../../../_shared/_logic/working-hours-view"
  import type { WorkingHoursByUser } from "../../../../../../../../../../_shared/_logic/form-state"
  import { filterSelectableOrganizationUsers } from "../../../../../../../../_shared/_logic/organization-users"
  import AppointmentAnnouncementPanel from "./_modules/appointment-announcement-panel"
  import { scheduleEventPriority } from "../../../../../../../../../../_shared/_logic/outreach-presentation"
  import {
    buildEmployeeOptions,
    clearRemovedScheduleEventIds,
    type EmployeeSource,
    markRemovedScheduleEventIds,
    mergeScheduleEvents,
    visibleScheduleEventsForWorkOrder,
  } from "../../../../_shared/_logic/work-order-page-helpers"
  import {
    loadAppointmentPlanningData,
    loadScheduleEventsForOrganization,
    loadWorkOrderCommunicationPlanningData,
  } from "../../../../../../../../../../api/kehrblatt/work-order-detail"
  import type {
    ScheduleEventCommunicationPresentation,
    WorkOrderPlanningContactOrderSummary,
  } from "../../../../../../../../../../api/kehrblatt/work-orders"

  import type { WorkOrderPageContext as PageContext } from "../../../../context"

  const { pageContext } = $props<{
    pageContext: PageContext
    activeTab?: string
  }>()

  const organizationId = $derived(pageContext.organizationId)
  const workOrderId = $derived(pageContext.workOrderId)
  const subscriptionRestrictionActive = $derived(Boolean(pageContext.subscriptionRestrictionActive))
  const onContextChange = $derived(pageContext.onContextChange ?? (() => {}))
  const permissions = $derived(pageContext.permissions)
  const canManageScheduling = $derived(permissions.allows("kehrblatt.scheduling", "manage"))
  const normalizeEmployeeSources = (value: unknown): EmployeeSource[] =>
    Array.isArray(value) ? (value.filter(Boolean) as EmployeeSource[]) : []

  let loading = $state(true)
  let error = $state<string | null>(null)
  let requestId = 0
  let workOrder = $state<WorkOrder | null>(null)
  let properties = $state<Property[]>([])
  let customers = $state<Customer[]>([])
  let scheduleEvents = $state<ScheduleEvent[]>([])
  let allScheduleEvents = $state<ScheduleEvent[]>([])
  let scheduleEventCommunicationPresentations = $state<
    Record<string, ScheduleEventCommunicationPresentation>
  >({})
  let removedScheduleEventIds = $state<string[]>([])
  let contactOrders = $state<WorkOrderPlanningContactOrderSummary[]>([])
  let contactPoints = $state<ContactPoint[]>([])
  let workingHoursByUser = $state<WorkingHoursByUser>({})
  let allEmployeeIds = $state<string[]>([])
  let organizationUsers = $state<EmployeeSource[]>([])

  const mergeScheduleEventList = (
    events: ScheduleEvent[],
    { replace = false }: { replace?: boolean } = {},
  ) => {
    const { all, visible } = mergeScheduleEvents({
      currentAll: allScheduleEvents,
      incoming: events,
      removedIds: removedScheduleEventIds,
      workOrderId: workOrder?.id ?? null,
      replace,
    })
    allScheduleEvents = all
    scheduleEvents = visible
    onContextChange({ scheduleEvents })
  }

  const resetRemovedScheduleEvents = () => {
    removedScheduleEventIds = []
    scheduleEvents = visibleScheduleEventsForWorkOrder(
      workOrder?.id ?? null,
      allScheduleEvents,
      removedScheduleEventIds,
    )
  }

  const markScheduleEventRemoved = (id?: string | null) => {
    removedScheduleEventIds = markRemovedScheduleEventIds(removedScheduleEventIds, id)

    if (!id) return

    allScheduleEvents = allScheduleEvents.filter((entry) => entry.id !== id)
    scheduleEvents = visibleScheduleEventsForWorkOrder(
      workOrder?.id ?? null,
      allScheduleEvents,
      removedScheduleEventIds,
    )
    onContextChange({ scheduleEvents })
  }

  const clearRemovedScheduleEvent = (id?: string | null) => {
    removedScheduleEventIds = clearRemovedScheduleEventIds(removedScheduleEventIds, id)
    scheduleEvents = visibleScheduleEventsForWorkOrder(
      workOrder?.id ?? null,
      allScheduleEvents,
      removedScheduleEventIds,
    )
  }

  const loadPlanning = async () => {
    if (!organizationId || !workOrderId) return

    const currentRequest = ++requestId
    loading = true
    error = null
    try {
      const data = await loadAppointmentPlanningData(organizationId, workOrderId)

      if (currentRequest !== requestId) return

      workOrder = data.workOrder
      properties = data.properties
      contactOrders = data.contactOrders
      contactPoints = data.contactPoints
      customers = data.customers
      scheduleEventCommunicationPresentations = data.scheduleEventCommunicationPresentations
      const activeUsers = filterSelectableOrganizationUsers(
        normalizeEmployeeSources(data.users),
        null,
      )
      organizationUsers = activeUsers
      workingHoursByUser = buildWorkingHoursByUser(activeUsers)
      allEmployeeIds = activeUsers.map((user) => user.id).filter(Boolean)
      resetRemovedScheduleEvents()
      mergeScheduleEventList(data.scheduleEvents, { replace: true })
      onContextChange({ workOrder, properties })
    } catch (err) {
      if (currentRequest !== requestId) return

      console.error("appointment_planning_load_failed", err)
      error = "Terminkommunikation konnte nicht geladen werden."

      // Lets the record header leave its loading state; a confirmed order stays on refresh errors.
      if (!workOrder) onContextChange({ workOrder: null })
    } finally {
      if (currentRequest === requestId) loading = false
    }
  }

  const reloadScheduleEvents = async ({ merge = true }: { merge?: boolean } = {}) => {
    if (!organizationId) return

    try {
      const refreshed = await loadScheduleEventsForOrganization(organizationId)
      mergeScheduleEventList(refreshed, { replace: !merge })
    } catch (err) {
      console.error("schedule_events_refresh_failed", err)
    }
  }

  const refreshPlanningContext = async () => {
    const requestedWorkOrderId = workOrderId

    if (!requestedWorkOrderId) return

    const data = await loadWorkOrderCommunicationPlanningData(requestedWorkOrderId)

    if (requestedWorkOrderId !== workOrderId) return

    contactOrders = data.contactOrders
    scheduleEventCommunicationPresentations = data.scheduleEventCommunicationPresentations
    mergeScheduleEventList(data.scheduleEvents, { replace: true })
  }

  const handleScheduleEventUpdated = async (
    updated?: ScheduleEvent | null,
    removedId?: string | null,
  ) => {
    if (removedId) {
      markScheduleEventRemoved(removedId)
    }

    if (updated?.id) {
      clearRemovedScheduleEvent(updated.id)
      mergeScheduleEventList([updated])
    } else if (!removedId) {
      await reloadScheduleEvents()

      return
    }

    await loadPlanning()
  }

  onMount(() => {
    void loadPlanning()

    return () => {
      requestId += 1
    }
  })

  const relevantScheduleEvent = $derived.by(() => {
    if (!workOrder?.id) return null

    const linked = scheduleEvents.filter((event) => event.work_order_id === workOrder.id)
    const candidates = linked.filter((event) => !["CANCELED", "NOSHOW"].includes(event.status))
    const events = candidates.length ? candidates : []

    if (!events.length) return null

    const sorted = [...events].sort((a, b) => {
      const priorityDiff =
        (scheduleEventPriority[b.status] ?? 0) - (scheduleEventPriority[a.status] ?? 0)

      if (priorityDiff !== 0) return priorityDiff

      const aStart = new Date(a.start_at).getTime()
      const bStart = new Date(b.start_at).getTime()

      if (!Number.isFinite(aStart) && Number.isFinite(bStart)) return 1

      if (!Number.isFinite(bStart) && Number.isFinite(aStart)) return -1

      return bStart - aStart
    })

    return sorted[0] ?? null
  })
  const relevantScheduleEventId = $derived(relevantScheduleEvent?.id ?? null)
  const effectiveCanEdit = $derived(
    Boolean(
      canManageScheduling &&
      !subscriptionRestrictionActive &&
      workOrder?.can_manage &&
      !workOrder.archived_at,
    ),
  )
  const employeeOptions = $derived.by(() =>
    buildEmployeeOptions(organizationUsers, allScheduleEvents),
  )
</script>

<span style="display: none;" aria-hidden="true"></span>

{#if loading}
  <div class="state-block">
    <p>Terminkommunikation wird geladen…</p>
  </div>
{:else if error}
  <div class="state-block">
    <p>{error}</p>
    <CTA type="secondaryButton" content="Erneut laden" onClick={loadPlanning}>
      {#snippet iconSnippet()}
        <Icon path={mdiRefresh} />
      {/snippet}
    </CTA>
  </div>
{:else}
  <AppointmentAnnouncementPanel
    {organizationId}
    {workOrder}
    {properties}
    {scheduleEvents}
    {allScheduleEvents}
    {scheduleEventCommunicationPresentations}
    {relevantScheduleEvent}
    {customers}
    employees={employeeOptions}
    {relevantScheduleEventId}
    {workingHoursByUser}
    {allEmployeeIds}
    {contactOrders}
    {contactPoints}
    canEdit={effectiveCanEdit}
    onReloadScheduleEvents={reloadScheduleEvents}
    onScheduleEventUpdated={handleScheduleEventUpdated}
    onPlanningContextInvalidated={refreshPlanningContext}
  />
{/if}

<style lang="less">
  .state-block {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;

    p {
      margin: 0;
    }
  }
</style>
